import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Target, Users, Shield } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import WaitlistCTA, { WaitlistCTAGroup } from '../components/WaitlistCTA';

export default function About() {
  const navigate = useNavigate();

  const goToWaitlist = (role) => {
    navigate('/?scroll=waitlist&role=' + role);
  };

  return (
    <div className="min-h-screen" data-testid="about-page">
      <PublicNav currentPage="about" />

      {/* Section 1: Hero - Light (below dark nav) */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-blue-600/10 text-blue-600 rounded-full text-xs font-medium mb-4 tracking-wide">
            MATCHMAKING · NOT FUNDING
          </div>
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">About SeedSynch</h1>
          <p className="text-lg text-[#334d6e]">Smarter connections, earlier.</p>
        </div>
      </section>

      {/* Section 2: Our Purpose - Dark */}
      <section className="bg-slate-800 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <Card className="bg-slate-700/50 border-slate-600 p-12">
            <h2 className="text-2xl font-bold text-white mb-6">Our Purpose</h2>
            <p className="text-base text-[#a3b8d1] leading-relaxed mb-6">
              SeedSynch helps founders and investors find each other earlier in the process—before formal pitches, before capital discussions. We focus on <strong className="text-blue-400">who</strong> connects with whom, based on declared preferences and criteria.
            </p>
            <p className="text-base text-[#a3b8d1] leading-relaxed mb-6">
              We exist to solve a specific problem: founders spend time pitching to investors with incompatible expectations. Investors filter through ventures that don't match their criteria. Conversations start without context. Time is wasted.
            </p>
            <p className="text-base text-[#a3b8d1] leading-relaxed">
              SeedSynch is a <strong className="text-white">matchmaking and discovery platform</strong>—not a funding platform, lender, or investment arranger. We facilitate introductions between founders and investors who share alignment on key criteria. What happens after the introduction is between the parties.
            </p>
          </Card>
        </div>
      </section>

      {/* Section 3: Features - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-slate-200 p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Target className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3">Preference-Based Matching</h3>
              <p className="text-[#4a6382] text-sm">Declared preferences and criteria—not just keywords—drive our matching. Conversations start with shared context.</p>
            </Card>

            <Card className="bg-white border-slate-200 p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3">Facilitation Only</h3>
              <p className="text-[#4a6382] text-sm">We introduce. We don't structure, advise, or certify. Users are responsible for their own arrangements.</p>
            </Card>

            <Card className="bg-white border-slate-200 p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Users className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-3">Focused Community</h3>
              <p className="text-[#4a6382] text-sm">A curated network of founders and investors focused on early-stage partnerships and meaningful alignment.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 4: Who We Serve - Dark */}
      <section className="bg-slate-800 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <Card className="bg-slate-700/50 border-slate-600 p-12">
            <h2 className="text-2xl font-bold text-white mb-6">Who We Serve</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#c7d4e4] mb-2">For Founders</h3>
                <p className="text-[#a3b8d1] text-sm">Discover investors who match your criteria. Filter by industry, stage, and structure preferences. Start conversations with relevant context.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#c7d4e4] mb-2">For Investors</h3>
                <p className="text-[#a3b8d1] text-sm">Find ventures that match your investment criteria. Filter by industry, stage, and geography. Access founders looking for aligned partnerships.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#c7d4e4] mb-2">For Co-founders</h3>
                <p className="text-[#a3b8d1] text-sm">Find founding teams that match your skills and expectations. Browse ventures seeking co-founders and filter by industry, equity range, and commitment level.</p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Section 5: CTA - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs text-[#4a6382] mb-12 max-w-2xl mx-auto">
            SeedSynch is a facilitation platform only. We do not provide financial advice or investment recommendations. Users are responsible for their own decisions and should consult qualified advisors for specific guidance.
          </p>
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Request Early Access</h2>
          <p className="text-base text-[#334d6e] mb-8">Join the waitlist to be first to know when private access opens.</p>
          <WaitlistCTAGroup 
            onFounderClick={() => goToWaitlist('founder')}
            onInvestorClick={() => goToWaitlist('investor')}
            theme="light"
            size="default"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
